import { useEffect, useState } from 'react';
import {
  Edit, SimpleForm, TextInput, useGetList, Loading, useNotify,
  useDataProvider, required,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Box, Button, CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/StarBorder';
import { ItalicHeadlineInput } from '../../components/ItalicHeadlineInput.jsx';

const RESOURCE = 'home-advantage';

// Default copy used when initialising the section for the first time.
const DEFAULTS = {
  eyebrow: 'Why Maple Leaf',
  rawTitle: 'The Maple Leaf <em>Advantage</em>',
  lede: 'A thoughtful approach to teaching and a clear focus on academic excellence. A strong community ethos and a safe, supportive learning environment. Maple Leaf students have all they need to succeed.',
};

/**
 * The Advantage section header is a singleton — exactly one row. The "list"
 * view immediately routes to the edit page for that row. If no row exists yet
 * (e.g. fresh DB), we offer a one-click initialise.
 */
const AdvantageRedirect = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data, isLoading } = useGetList(RESOURCE, {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      navigate(`/${RESOURCE}/${data[0].id}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) return <Loading />;
  if (data && data.length > 0) return null;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await dataProvider.create(RESOURCE, { data: { ...DEFAULTS } });
      navigate(`/${RESOURCE}/${created.data.id}`, { replace: true });
    } catch (err) {
      notify(`Failed to initialise Advantage section: ${err.message}`, { type: 'error' });
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Advantage section not yet initialised</Typography>
      <Button variant="contained" onClick={handleCreate} disabled={creating}>
        {creating ? <CircularProgress size={18} /> : 'Initialise Advantage section'}
      </Button>
    </Box>
  );
};

const AdvantageForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <Typography variant="overline" color="text.secondary">
      Homepage — "Why Maple Leaf" section header
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
      The headline + intro shown above the feature accordion on the homepage.
      The accordion's cards are managed under "Why Maple Leaf" in the sidebar.
    </Typography>
    <TextInput
      source="eyebrow"
      label="Eyebrow"
      fullWidth
      helperText='Small uppercase label above the title — e.g. "Why Maple Leaf".'
    />
    <ItalicHeadlineInput source="rawTitle" label="Title" validate={required()} />
    <TextInput
      source="lede"
      label="Intro paragraph"
      multiline
      minRows={4}
      fullWidth
      helperText="The supporting paragraph shown under the title."
    />
  </Stack>
);

const AdvantageEdit = () => (
  <Edit redirect={false} mutationMode="pessimistic" actions={false}>
    <SimpleForm><AdvantageForm /></SimpleForm>
  </Edit>
);

export default {
  list: AdvantageRedirect,
  edit: AdvantageEdit,
  icon: StarIcon,
  options: { label: 'Advantage' },
};
